/**
 * User Profile Modal Component
 *
 * Displays user profile information in a modal
 */
import React from 'react';
import { Modal, Avatar, Descriptions, Tag, Space } from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useUserProfile } from '../hooks/useUserProfile';
import type { UserProfileModalProps } from '../types';
import '../styles/UserProfileModal.css';
import { getInitials } from '@/features/user-management/utils';

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  open,
  onClose,
}) => {
  const { user } = useUserProfile();

  if (!user) {
    return null;
  }
  // Get role color
  const getRoleColor = (role?: string) => {
    if (!role) {
      return 'default';
    }
    const roleColors: Record<string, string> = {
      admin: 'red',
      manager: 'blue',
      user: 'green',
    };
    return roleColors[role.toLowerCase()] || 'default';
  };

  return (
    <Modal
      title="User Profile"
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
      className="user-profile-modal"
    >
      <div className="user-profile-modal__content">
        {/* Avatar Section */}
        <div className="user-profile-modal__header">
          <Avatar
            size={80}
            className="user-profile-modal__avatar"
            src={user.avatar}
          >
            {!user.avatar && getInitials(user.userName || '')}
          </Avatar>
          <div className="user-profile-modal__header-info">
            <h3 className="user-profile-modal__name">{user.userName}</h3>
            <Tag
              color={getRoleColor(user.role)}
              className="user-profile-modal__role"
            >
              {user.role}
            </Tag>
          </div>
        </div>

        {/* User Information */}
        <Descriptions
          column={1}
          bordered
          className="user-profile-modal__descriptions"
        >
          <Descriptions.Item
            label={
              <Space>
                <UserOutlined />
                <span>User ID</span>
              </Space>
            }
          >
            {user.userId}
          </Descriptions.Item>

          <Descriptions.Item
            label={
              <Space>
                <MailOutlined />
                <span>Email</span>
              </Space>
            }
          >
            {user.email || 'N/A'}
          </Descriptions.Item>

          {user.phone && (
            <Descriptions.Item
              label={
                <Space>
                  <PhoneOutlined />
                  <span>Phone</span>
                </Space>
              }
            >
              {user.phone}
            </Descriptions.Item>
          )}

          {user.department && (
            <Descriptions.Item
              label={
                <Space>
                  <TeamOutlined />
                  <span>Department</span>
                </Space>
              }
            >
              {user.department}
            </Descriptions.Item>
          )}

          {user.companyName && (
            <Descriptions.Item label="Company">
              {user.companyName}
            </Descriptions.Item>
          )}

          <Descriptions.Item label="Company Code">
            {user.companyCode}
          </Descriptions.Item>
        </Descriptions>
      </div>
    </Modal>
  );
};
